import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosSetup.js';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { closeModal } from '../../../action/modalbehaviour.js';
import { calculateTotalTime, validateTime } from '../../../action/timeCalculation.jsx';

const EditTimeLog = ({timelogId, list}) => {
    const [timelogdata, setTimeLogData] = useState([]);
    const [taskTypes, setTaskTypes] = useState([]);
    const [taskIncharge, setTaskIncharge] = useState([]);
    const [getChannel, setChannel] = useState('');
    const [meter, setMeter] = useState([]);
    const [getMeterType, setMeterType] = useState('');

    const [taskTypeId, setTaskTypeId] = useState('');
    const [taskInchargeId, setTaskInchargeId] = useState('');

    const [timeSpendHours, setTimeSpendHours] = useState('');
    const [timeSpendMinutes, setTimeSpendMinutes] = useState('');
    const [billableHours, setBillableHours] = useState('');
    const [billableMinutes, setBillableMinutes] = useState('');
    const [nonBillableHours, setNonBillableHours] = useState('');
    const [nonBillableMinutes, setNonBillableMinutes] = useState('');
    const [timeSpendTotal, setTimeSpendTotal] = useState('');

    //CHECK METER IS HOURLY OR FIXED
    const handleMeterChange = async (meter) => {
        try {
            if(meter !== '' && meter !== null){
                var project = document.getElementById('project_id').value;
                const response = await axiosInstance.post(`${process.env.REACT_APP_API_URL}/check_meter`, {
                    meter_id       : meter,
                    project_id     : project,
                });

                const jsonData = response.data;
                setMeterType(jsonData.success === true ? jsonData.data : 'Hourly');                
            }else{
                setMeterType('');
            }
        }
        catch (error) {
            console.log(error.message);
        }
    }

    const fetchTimeLogData = async (Id) => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/get_timelog/${Id}`)
            .then(response => {
                const jsonData = response.data.data;
                const timelogData = jsonData.timelog;
                setTimeLogData(timelogData);
                setChannel(timelogData.channel);
                setMeter(jsonData.meter);
                setTaskTypes(jsonData.task_types);
                setTimeSpendHours(timelogData.time_hours);
                setTimeSpendMinutes(timelogData.time_minutes);
                setBillableHours(timelogData.bill_hours);
                setBillableMinutes(timelogData.bill_minutes);
                setNonBillableHours(timelogData.non_bill_hours);
                setNonBillableMinutes(timelogData.non_bill_minutes);

                handleMeterChange(timelogdata.meter_id || null);
            })
            .catch(error => {
                console.error('Error fetching timelog', error);
                setTimeLogData([]);
                setChannel('');
                setMeter([]);
                setTaskTypes([]);
                setTimeSpendHours('');
                setTimeSpendMinutes('');
                setBillableHours('');
                setBillableMinutes('');
                setNonBillableHours('');
                setNonBillableMinutes('');
                setTimeSpendTotal('');
            });
    }
    
    const fetchTaskIncharge = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/users?users=teamleads`)
            .then(response => {
                setTaskIncharge(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching task incharge', error);
                setTaskIncharge([]);
            });
    };

    useEffect(() => {
        if (timelogId) {
            fetchTimeLogData(timelogId);
            fetchTaskIncharge();
        }
    }, [timelogId]);

    useEffect(() => {
        const Total = calculateTotalTime(timeSpendHours, timeSpendMinutes);
        setTimeSpendTotal(Total);
    }, [timeSpendHours, timeSpendMinutes]);

    // VALIDATION RULES
    const schema = Yup.object().shape({
        time_log_date: Yup.string().required('date is a required field'),
        task_type_id: Yup.string().required('task type is a required field'),
        time_spend: Yup.string().nullable().typeError('time spent is a required field')
            .when('$timelog', {
                is: value => value === '' || value === 0,
                then: () => Yup.string().required('time spent is a required field'),
            }),
        meter_id: Yup.string().when('$channel', {
            is: 'Upwork',
            then: () => Yup.string().required('meter is a required field'),
        })
    });

    const { register, handleSubmit, formState: { errors }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(schema),
        context: { channel: getChannel, timelog: timeSpendTotal}
    });
    
    useEffect(() => {
        if (timelogdata) {
            reset(timelogdata);
            setTaskTypeId(timelogdata.task_type_id || '');
            setTaskInchargeId(timelogdata.task_incharge || '');
        }

        const modalElement = document.getElementById(`edit_timelog_${timelogId}`);

        const handleClose = () => {
            reset();
            setMeterType('');
            setTimeSpendHours('');
            setTimeSpendMinutes('');
            setBillableHours('');
            setBillableMinutes('');
            setNonBillableHours('');
            setNonBillableMinutes('');
            setTimeSpendTotal('');
        }

        if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', handleClose);
        }
        return () => {
            if (modalElement) {
                modalElement.removeEventListener('hidden.bs.modal', handleClose);
            }
        };
    }, [timelogdata, timelogId, reset]);


    const onSubmit = async (data) => {
        if (!validateTime(timeSpendHours, timeSpendMinutes, billableHours, billableMinutes, nonBillableHours, nonBillableMinutes)) {
            Swal.fire({ text: 'Total time spent must equal the sum of billable and non-billable time.', icon: 'error' });
            return;
        }

        axiosInstance.post(`${process.env.REACT_APP_API_URL}/update_timelog/${timelogId}`, data)
            .then(response => {
                Swal.fire({ icon:"success", text:response.data.message});
                closeModal(`edit_timelog_${timelogId}`);
                reset();
                list();
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
                console.error('Error updating time log', data);
            });
    }
   
    if (!timelogId || !timelogdata) {
        return null;
    }

    const labelText = getChannel === 'Upwork' ? (getMeterType === 'Fixed' ? 'Fixed ': ' ') 
                                : (getChannel === 'Visions' ? ' ' : 'Fixed ');

    const handleTaskTypeChange = (event) => {
        setTaskTypeId(event.target.value);
    };

    const handleInchargeChange = (event) => {
        setTaskInchargeId(event.target.value);
    };

    return (
        <div id={`edit_timelog_${timelogId}`} className="modal custom-modal fade" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Update Time</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <input type="hidden" value={timelogdata.project_id || ""} id="project_id" {...register('project_id')}/>
                                <input type="hidden" value={timelogdata.basic_project_id || ""} {...register('basic_project_id')}/>

                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Channel</label>
                                        <input type="text" className="form-control" value={getChannel ?? ""} disabled />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Client</label>
                                        <input type="text" className="form-control" value={timelogdata.client ?? ""} disabled />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Project</label>
                                        <input type="text" className="form-control" value={timelogdata.project ?? ""} disabled />
                                    </div>
                                </div>

                                { getChannel === 'Upwork' &&
                                    <div className={getChannel === 'Upwork' ? 'col-sm-4' : 'col-sm-6'}>
                                        <div className="input-block mb-3">
                                            <label className="col-form-label">Meter <span className="text-danger">*</span></label>
                                            <select className="form-control" defaultValue={timelogdata.meter_id || ""} 
                                                    {...register('meter_id')} onChange={e => handleMeterChange(e.target.value)}>
                                                <option value="">Select Meter</option>
                                                {meter.map(met => (
                                                    <option key={met.id} value={met.id}>{met.name}</option>
                                                ))}
                                            </select>
                                            <div className="text-sm text-danger">
                                                {errors.meter_id && <p>{errors.meter_id.message}</p>}
                                            </div>
                                        </div>
                                    </div>
                                }

                                <div className={getChannel === 'Upwork' ? 'col-sm-4' : 'col-sm-6'}>
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Task Type<span className="text-danger">*</span></label>
                                        <select className="form-control" {...register('task_type_id', { value: taskTypeId, onChange: handleTaskTypeChange })} >
                                            <option value="">Select Task Type</option>
                                            {taskTypes.map(task => (
                                                <option key={task.id} value={task.id}>{task.title}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.task_type_id && <p>{errors.task_type_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className={getChannel === 'Upwork' ? 'col-sm-4' : 'col-sm-6'}>
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Date <span className="text-danger">*</span></label>
                                        <input className="form-control" type="date" defaultValue={timelogdata.time_log_date || ''} {...register('time_log_date')} />
                                        <div className="text-sm text-danger">
                                            {errors.time_log_date && <p>{errors.time_log_date.message}</p>}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Time Spend <span className="text-danger">*</span></label>
                                        <div className="row">
                                            <div className="col-sm-6">
                                                <div className="row">
                                                    <div className="col-sm-3">
                                                        <span className="form-text text-muted">Hours</span>
                                                        <select className="form-control form-control-sm" {...register('time_spend', 
                                                                { value: timelogdata.time_hours || '',
                                                                onChange: e => setTimeSpendHours(e.target.value) })} >
                                                            <option value="">00</option>
                                                            <option value="01">01</option>
                                                            <option value="02">02</option>
                                                            <option value="03">03</option>
                                                            <option value="04">04</option>
                                                            <option value="05">05</option>
                                                            <option value="06">06</option>
                                                            <option value="07">07</option>
                                                            <option value="08">08</option>
                                                            <option value="09">09</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-sm-3">
                                                        <span className="form-text text-muted">Minute</span>
                                                        <select className="form-control form-control-sm" {...register('time_minutes', 
                                                                { value: timelogdata.time_minutes || '', 
                                                                onChange: e => setTimeSpendMinutes(e.target.value) })} >
                                                            <option value="00">00</option>
                                                            <option value="17">10</option>
                                                            <option value="33">20</option>
                                                            <option value="50">30</option>
                                                            <option value="67">40</option>
                                                            <option value="83">50</option>
                                                        </select>
                                                    </div>
                                                    <div className="text-sm text-danger">
                                                        {errors.time_spend && <p>{errors.time_spend.message}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">{labelText} Billable Time <span className="text-danger">*</span></label>
                                        <div className="row">
                                            <div className="col-sm-3">
                                                <span className="form-text text-muted">Hours</span>
                                                <select className="form-control form-control-sm" {...register('billable_time', 
                                                        { value: timelogdata.bill_hours || '', 
                                                        onChange: e => setBillableHours(e.target.value) })} >                                                        
                                                    <option value="">00</option>
                                                    <option value="01">01</option>
                                                    <option value="02">02</option>
                                                    <option value="03">03</option>
                                                    <option value="04">04</option>
                                                    <option value="05">05</option>
                                                    <option value="06">06</option>
                                                    <option value="07">07</option>
                                                    <option value="08">08</option>
                                                    <option value="09">09</option>
                                                </select>
                                            </div>
                                            <div className="col-sm-3">
                                                <span className="form-text text-muted">Minute</span>                                                
                                                <select className="form-control form-control-sm" {...register('billable_minutes', 
                                                        { value: timelogdata.bill_minutes || '', 
                                                        onChange: e => setBillableMinutes(e.target.value) })} >
                                                    <option value="00">00</option>
                                                    <option value="17">10</option>
                                                    <option value="33">20</option>
                                                    <option value="50">30</option>
                                                    <option value="67">40</option>
                                                    <option value="83">50</option>
                                                </select>
                                            </div>
                                        </div> 
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Non Billable Time </label>
                                        <div className="row">
                                            <div className="col-sm-3">
                                                <span className="form-text text-muted">Hours</span>
                                                <select className="form-control form-control-sm" {...register('nonbillable_time', 
                                                        { value: timelogdata.non_bill_hours || '', 
                                                        onChange: e => setNonBillableHours(e.target.value) })} >
                                                    <option value="">00</option>
                                                    <option value="01">01</option>
                                                    <option value="02">02</option>
                                                    <option value="03">03</option>
                                                    <option value="04">04</option>
                                                    <option value="05">05</option>
                                                    <option value="06">06</option>
                                                    <option value="07">07</option>
                                                    <option value="08">08</option>
                                                    <option value="09">09</option>
                                                </select>
                                            </div>
                                            <div className="col-sm-3">
                                                <span className="form-text text-muted">Minute</span>
                                                <select className="form-control form-control-sm" {...register('nonbillable_minutes', 
                                                        { value: timelogdata.non_bill_minutes || '',
                                                        onChange: e => setNonBillableMinutes(e.target.value) })} >
                                                    <option value="00">00</option>
                                                    <option value="17">10</option>
                                                    <option value="33">20</option>
                                                    <option value="50">30</option>
                                                    <option value="67">40</option>
                                                    <option value="83">50</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                

                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">{labelText} Billable Description <span className="text-danger">*</span></label>
                                        <textarea className="form-control" defaultValue={timelogdata.billable_description || ""} 
                                                {...register('billable_description')} rows={3}></textarea>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Non Billable Description <span className="text-danger">*</span></label>
                                        <textarea className="form-control" defaultValue={timelogdata.nonbillable_description || ""} 
                                                {...register('nonbillable_description')} rows={3}></textarea>
                                    </div>
                                </div>
                                
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Select Manager/Team Lead</label>
                                        <select className="form-control" value={taskInchargeId} {...register('task_incharge', { onChange: handleInchargeChange })}>
                                            <option value="">Select Manager/Team Lead</option>
                                            {taskIncharge.map(tl => (
                                                <option key={tl.id} value={tl.id}>{tl.name} ({tl.designation})</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="submit-section">
                                <button type="submit" className="btn btn-primary submit-btn">SUBMIT</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditTimeLog